<template>
  <div class="main-container">
    <Header
      title="Llamadas de Servicio"
      sub-title="Gestión de solicitudes y llamadas de servicio"
    >
      <div class="d-flex align-items-center justify-content-end">
        <b-button
          variant="outline-primary"
          @click="$router.push('/app/prod/llamadas-servicio/nueva')"
        >
          <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
          <span class="pl-1">Crear OS</span>
        </b-button>
      </div>
    </Header>

    <b-container fluid class="index-body">
      <!-- Search bar and filters -->
      <b-row class="mt-3">
        <b-col md="6">
          <div class="d-flex align-items-center">
            <b-form-group style="min-width: 88%" class="mb-0">
              <b-input-group>
                <b-input
                  v-model="searchTerm"
                  autofocus
                  :placeholder="searchByHints[serviceType]"
                  @keyup.enter="search"
                />
                <b-input-group-append class="mb-0">
                  <b-button variant="outline-secondary" @click="search">
                    <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
            <b-form-group class="ml-3 mb-0">
              <b-button variant="outline-secondary" @click="resetSearch">
                <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
              </b-button>
            </b-form-group>
          </div>
        </b-col>
        <b-col md="6" align-self="end">
          <div class="d-flex justify-content-end mt-2 mt-md-0">
            <b-dropdown
              id="filter-dropdown"
              ref="filterDropdown"
              dropleft
              variant="primary"
              no-caret
            >
              <template #button-content>
                <div class="d-flex align-items-center">
                  <font-awesome-icon :icon="['far', 'filter']" fixed-width />
                  <span class="ml-2 mr-2">Filtros</span>
                  <div id="filter-count">
                    <span>{{ filters.length }}</span>
                  </div>
                </div>
              </template>
              <b-dropdown-form style="width: 19rem;">
                <b-form-group>
                  <template #label><span>Categoría del servicio</span>
                  </template>
                  <b-select
                    :value="filtersForm.sap_series"
                    :options="sapSeriesOptions"
                    @input="updateFilter('sap_series', $event)"
                  >
                    <template #first>
                      <option :value="null">-- Seleccione --</option>
                    </template>
                  </b-select>
                </b-form-group>

                <b-form-group>
                  <template #label><span>Unidad de negocio</span></template>
                  <b-select
                    :value="filtersForm.call_type"
                    :options="businessUnitOptions"
                    @input="updateFilter('call_type', $event)"
                  >
                    <template #first>
                      <option :value="null">-- Seleccione --</option>
                    </template>
                  </b-select>
                </b-form-group>

                <b-form-group>
                  <template #label><span>Tipo de equipo</span></template>
                  <b-select
                    :value="filtersForm.system_type"
                    :options="equipmentTypeOptions"
                    @input="updateFilter('system_type', $event)"
                  >
                    <template #first>
                      <option :value="null">-- Seleccione --</option>
                    </template>
                  </b-select>
                </b-form-group>
              </b-dropdown-form>
            </b-dropdown>
          </div>
        </b-col>
      </b-row>

      <b-row v-show="filters.length > 0" class="mt-2">
        <b-col>
          <div class="d-flex align-items-center justify-content-end" style="gap: 0.5rem;">
            <div
              id="filter-item"
              v-for="filter in filters"
              :key="filter.key"
              class="d-flex align-items-center fs-13"
            >
              <span>{{ filter.text }}</span>
              <button
                type="button"
                class="btn btn-sm p-0"
                @click="removeFilter(filter.key)"
              >
                <font-awesome-icon :icon="['far', 'times']" fixed-width />
              </button>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col cols="12" md="8" class="mb-0 mb-lg-2">
          <b-button-group>
            <b-button
              :variant="serviceType === 'service_call_requests' ? 'primary' : 'outline-light'"
              :class="{ 'text-dark': serviceType !== 'service_call_requests' }"
              @click="onServiceTypeChange('service_call_requests')"
            >
              <span v-if="isOsGestor">Solicitudes de OS</span>
              <span v-else>Mis solicitudes</span>
            </b-button>
            <b-button
              :variant="serviceType === 'unscheduled' ? 'primary' : 'outline-light'"
              :class="{ 'text-dark': serviceType !== 'unscheduled' }"
              @click="onServiceTypeChange('unscheduled')"
            >
              Por agendar
            </b-button>
            <b-button
              :variant="serviceType === 'scheduled' ? 'primary' : 'outline-light'"
              :class="{ 'text-dark': serviceType !== 'scheduled' }"
              @click="onServiceTypeChange('scheduled')"
            >
              Coordinadas
            </b-button>
            <b-button
              :variant="serviceType === 'onSite' ? 'primary' : 'outline-light'"
              :class="{ 'text-dark': serviceType !== 'onSite' }"
              @click="onServiceTypeChange('onSite')"
            >
              En sitio
            </b-button>
            <b-button
              :variant="serviceType === 'Delivered' ? 'primary' : 'outline-light'"
              :class="{ 'text-dark': serviceType !== 'Delivered' }"
              @click="onServiceTypeChange('Delivered')"
            >
              Entregadas
            </b-button>
          </b-button-group>
        </b-col>
        <b-col cols="12" md="4" align-self="end" class="mb-0 mb-lg-2 mt-0 mt-lg-2">
          <div class="d-flex justify-content-end text-muted align-items-end">
            <p class="mb-0 fs-15">
              Resultados:
            </p>
            <p class="fs-15 mb-0 ml-2" style="min-width: 20px;">{{ count }}</p>
          </div>
        </b-col>
      </b-row>

      <!-- Table -->
      <b-row class="d-none d-xl-block">
        <b-col>
          <b-table
            :items="items"
            :fields="fields"
            class="fs-15"
            show-empty
            caption-top
            hover
            responsive
            no-local-sorting
            :tbody-tr-class="rowClass"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            @sort-changed="sortColumn"
            @row-clicked="onRowClick"
          >
            <template #empty>
              <div v-if="loading" class="text-center text-secondary">
                <b-spinner small type="grow"></b-spinner>
                <span class="pl-2 fs-18">Cargando {{ serviceTypeMap[serviceType] }}</span>
              </div>
              <div v-else class="text-center font-italic">
                <span v-if="searchTerm" class="text-danger fs-18">No hay resultados</span>
                <span v-else class="text-secondary fs-18">No hay {{ serviceTypeMap[serviceType] }} </span>
              </div>
            </template>

            <template #cell(sap_doc_num)="data">
              <span class="text-primary font-weight-bold">{{ data.value }}</span>
            </template>

            <template #cell(created_by_id)="data">
              <NameInitialsIcon :user="data.item.created_by" :color="data.item.color" />
            </template>

            <!-- service_calls -->
            <template #cell(client_id)="data">
              <div class="d-flex justify-content-center flex-column" style="line-height: 19px;">
                <p class="mb-0 font-weight-bold" style="color: #333">
                  {{ data.item.client.client_num }}
                </p>

                <p class="mb-0 text-muted fs-15 text-uppercase">
                  {{ data.item.client.name }}
                </p>
              </div>
            </template>

            <!-- service_call_requests -->
            <template #cell(client_name)="data">
              <div class="d-flex justify-content-center flex-column" style="line-height: 19px;">
                <p v-if="data.item.client_id" class="mb-0 font-weight-bold" style="color: #333">
                  <span>{{ data.item.client.client_num }}</span>
                </p>
                <!-- <b-badge v-else class="fs-12 mb-1" pill variant="info" style="max-width: 96px;">
                  No registrado
                </b-badge> -->

                <p class="mb-0 text-muted fs-15">
                  <span v-if="data.item.client_id">{{ data.item.client.name }}</span>
                  <span v-else class="text-uppercase">{{ data.item.client_name }}</span>
                </p>
              </div>
            </template>

            <template #cell(sap_series)="data">
              <span>{{ sapSeriesMap[data.value] || '-' }}</span>
            </template>

            <template #cell(call_type)="data">
              <span>{{ callTypeMapByCode[data.value] || '-' }}</span>
            </template>

            <template #cell(subject)="data">
              <span>{{  data.value.length > 200 ? data.value.substring(0, 200) + '...' : data.value }}</span>
            </template>

            <template #cell(created_at)="data">
              <span>{{ data.value | formatDate }}</span>
            </template>
            <template #cell(scheduled_at)="data">
              <span>{{ data.item.event.start_at | formatDateTime }}</span>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <b-row class="mt-2 d-xl-none">
        <b-col
          v-for="item in items"
          :key="item.id"
          cols="12"
          class="mb-3"
        >
          <b-card
            class="shadow-sm"
            @click="onRowClick(item)"
            style="cursor: pointer; border-radius: 10px;"
          >
            <div class="header">
              <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex justify-content-center flex-column" style="line-height: 19px;">
                  <p v-if="item.client_id" class="mb-0 font-weight-bold" style="color: #333">
                    <span>{{ item.client.client_num }}</span>
                  </p>
                  <!-- <b-badge v-else class="fs-12 mb-1" pill variant="info" style="max-width: 96px;">
                    No registrado
                  </b-badge> -->

                  <p class="mb-0 text-muted fs-15">
                    <span v-if="item.client_id">{{ item.client.name }}</span>
                    <span v-else class="text-uppercase">{{ item.client_name }}</span>
                  </p>
                </div>
                <div class="text-muted small text-right d-flex flex-column justify-content-start align-items-end" style="align-self: baseline;">
                  <div class="d-flex justify-content-end" style="width: 90px;">
                    <span class="mr-2"><font-awesome-icon icon="calendar-alt" />
                      {{ item.created_at | formatDate }}
                    </span>
                  </div>
                  <div v-if="item.created_by_id">
                    {{ item.created_by | concatName2 | titleize }}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <p class="mb-0 mt-2 fs-17">
                <span v-if="item.subject">{{  item.subject.length > 200 ? item.subject.substring(0, 200) + '...' : item.subject }}</span>
                <span v-else> Sin descripción del problema </span>
              </p>
            </div>
          </b-card>
        </b-col>

        <b-col v-if="!items.length && !loading">
          <p class="text-center text-muted font-italic">
            <span v-if="searchTerm" class="text-danger fs-18">No hay resultados</span>
            <span v-else class="fs-18">No hay {{ serviceTypeMap[serviceType] }}</span>
          </p>
        </b-col>
        <b-col v-if="loading">
          <div class="text-center text-secondary">
            <b-spinner small type="grow"></b-spinner>
            <span class="pl-2 fs-18">Cargando {{ serviceTypeMap[serviceType] }}</span>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination -->
      <b-row>
        <b-col>
          <div class="d-flex justify-content-center my-3">
            <PaginationCountless :page="page" :next="next" :prev="prev">
              <template #prev-label>
                <font-awesome-icon :icon="['fas', 'backward']" size="lg" fixed-width />
              </template>
              <template #next-label>
                <font-awesome-icon :icon="['fas', 'forward']" size="lg" fixed-width />
              </template>
            </PaginationCountless>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { normalize, schema } from 'normalizr'
import { mapGetters } from 'vuex'

import Header from './Header'
import PaginationCountless from '../../shared/PaginationCountless'
import NameInitialsIcon from '../../shared/NameInitialsIcon'
import {
  sapSeriesOptions,
  businessUnits,
  equipmentTypeOptions,
  getSapSeriesText,
  getBusinessUnitText,
  callTypeMapByCode,
  sapSeriesMap
} from '../../../sharedOptions/serviceCallModule'

export default {
  name: "Index",
  components: { PaginationCountless, NameInitialsIcon, Header },
  data() {
    return {
      loading: false,
      searchTerm: '',
      searching: false,
      page: 1,
      next: null,
      prev: null,
      count: 0,
      serviceCallData: {},
      serviceCallList: [],
      serviceTypeMap: {
        'service_call_requests': 'Solicitudes de OS',
        'service_calls': 'Llamadas de Servicio',
        'unscheduled': 'OS pendientes de agendar',
        'scheduled': 'OS coordinadas',
        'onSite': 'OS en sitio',
        'Delivered': 'OS entregadas'
      },
      fieldsMap: {
        'service_call_requests': [
          { key: 'created_by_id', label: 'Creada por', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato', thStyle: { width: '270px' } },
          { key: 'client_name', label: 'Cliente', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'sap_series', label: 'Serie', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'call_type', label: 'UN', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'subject', label: 'Problema', tdClass: 'align-middle clickable', thClass: 'align-middle  text-muted font-lato', thStyle: { minWidth: '470px' } },
          { key: 'created_at', label: 'Creada', sortable: true, tdClass: 'text-center align-middle clickable', thClass: 'align-middle text-center text-muted font-lato', thStyle: { width: '120px' } }
        ],
        'unscheduled': [
          { key: 'sap_doc_num', label: '# servicio', sortable: true, tdClass: 'align-middle clickable text-center fs-14', thClass: 'align-middle text-muted font-lato text-center', thStyle: { width: '100px' } },
          { key: 'client_id', label: 'Cliente', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'sap_series', label: 'Serie', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'call_type', label: 'UN', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'subject', label: 'Problema', tdClass: 'align-middle clickable', thClass: 'align-middle  text-muted font-lato', thStyle: { minWidth: '470px' } },
          { key: 'created_at', label: 'Creada', sortable: true, tdClass: 'text-center align-middle clickable', thClass: 'align-middle text-center text-muted font-lato', thStyle: { width: '120px' } }
        ],
        'scheduled': [
          { key: 'sap_doc_num', label: '# servicio', sortable: true, tdClass: 'align-middle clickable text-center fs-14', thClass: 'align-middle text-muted font-lato text-center', thStyle: { width: '100px' } },
          { key: 'client_id', label: 'Cliente', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'sap_series', label: 'Serie', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'call_type', label: 'UN', sortable: true, tdClass: 'align-middle clickable', thClass: 'align-middle text-muted font-lato'},
          { key: 'subject', label: 'Problema', tdClass: 'align-middle clickable', thClass: 'align-middle  text-muted font-lato', thStyle: { minWidth: '470px' } },
          { key: 'scheduled_at', label: 'Agenda', sortable: false, tdClass: 'text-center align-middle clickable', thClass: 'align-middle text-center text-muted font-lato', thStyle: { width: '160px' } },
          { key: 'created_at', label: 'Creada', sortable: true, tdClass: 'text-center align-middle clickable', thClass: 'align-middle text-center text-muted font-lato', thStyle: { width: '120px' } },
        ]
      },
      searchByHints: {
        'service_call_requests': 'Buscar por solicitante o cliente',
        'service_calls': 'Buscar por cliente o # de servicio',
        'unscheduled': 'Buscar por cliente o # de servicio',
        'scheduled': 'Buscar por cliente o # de servicio',
        'onSite': 'Buscar por cliente o # de servicio',
        'Delivered': 'Buscar por cliente o # de servicio'
      },
      sapSeriesOptions: sapSeriesOptions,
      businessUnits : businessUnits,
      equipmentTypeOptions: equipmentTypeOptions,
      callTypeMapByCode: callTypeMapByCode,
      sapSeriesMap: sapSeriesMap
    }
  },
  mounted() {
    this.scrollToTop()
    this.fetchServiceCalls()
  },
  watch: {
    '$route.query' (to, from) {
      this.fetchServiceCalls()
    }
  },
  computed: {
    ...mapGetters(['isOsGestor']),
    items () {
      if (this.loading) return []

      const serviceCallList = structuredClone(this.serviceCallList)

      return serviceCallList.map(id => {
        const os = this.serviceCallData[id]

        this.$store.commit('setUserColor', os.created_by_id)
        os.color = this.$store.getters['userColorById'](os.created_by_id)

        return os
      })
    },
    fields () {
      return this.fieldsMap[this.serviceType] || this.fieldsMap['unscheduled']
    },
    serviceType: {
      get() {
        return this.$store.state.serviceCalls.serviceType
      },
      set(val) {
        this.$store.commit('serviceCalls/setServiceType', val)
      }
    },
    sortBy: {
      get() {
        return this.$store.state.serviceCalls.sortBy
      },
      set(val) {
        this.$store.commit('serviceCalls/setSortBy', val)
      }
    },
    sortDesc: {
      get() {
        return this.$store.state.serviceCalls.sortDesc
      },
      set(val) {
        this.$store.commit('serviceCalls/setSortDesc', val)
      }
    },
    filtersForm: {
      get() {
        return this.$store.state.serviceCalls.filters
      },
      set(_val) {}
    },
    businessUnitOptions() {
      return this.businessUnits.sort((a, b) => a.text.localeCompare(b.text, 'es'))
    },
    filters () {
      const selectedFilters = Object.entries(this.$store.state.serviceCalls.filters).filter(([key, value]) => value !== null)
      return selectedFilters.map(([key, value]) => {
        let text = value
        if (key === 'sap_series') {
          text = getSapSeriesText(value)
        } else if (key === 'call_type') {
          text = getBusinessUnitText(value)
        }
        return {
          key,
          value,
          text: text
        }
      })
    }
  },
  methods: {
    scrollToTop (){
      window.scrollTo({
        top: 70,
        left: 0,
        behavior: 'smooth'
      })
    },
    fetchServiceCalls () {
      if (this.loading) { return }
      this.loading = true
      this.$http.get('/api/production/service_calls', {
        params: {
          page: this.$route.query.pagina,
          service_type: this.serviceType,
          q: this.searchTerm,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          ...this.$store.state.serviceCalls.filters
        }
      })
        .then(response => {
          // console.log(response)
          const { service_calls, pagy } = response.data
          this.formatData(service_calls)
          this.addPagination(pagy)
        })
        .catch(error => {
          this.formatData([])
          console.log(error)
        })
        .finally(() => {
          this.loading = false
          this.searching = false
        })
    },
    search () {
      if (this.searching) { return }
      this.searching = true
      let query = Object.assign({}, this.$route.query)
      if (query.pagina) {
        delete query.pagina
        this.$router.replace({ query })
          .catch( (error)=>{
            if(error.name !== 'NavigationDuplicated' && !error.message.includes('Avoided redundant navigation to current location')){
              console.log(error)
            }
        })
      } else {
        this.fetchServiceCalls()
      }
    },
    formatData (serviceCalls) {
      const serviceCallData = normalize(
        { items: serviceCalls },
        { items: [ new schema.Entity('items') ] }
      )

      if (serviceCallData.entities.hasOwnProperty('items')) {
        this.serviceCallData = serviceCallData.entities.items
      } else {
        this.serviceCallData = {}
      }
      this.serviceCallList = serviceCallData.result.items
    },
    addPagination(pagy) {
      this.page = pagy.page
      this.count = pagy.count
      if (pagy.page < pagy.last) {
        this.next = pagy.page + 1
      } else {
        this.next = null
      }
      if (pagy.page != 1 && pagy.page > 1) {
        this.prev = pagy.page - 1
      } else {
        this.prev = null
      }
    },
    resetSearch () {
      this.searchTerm = ''
      this.search()
    },
    onServiceTypeChange (filter) {
      if (filter === this.serviceType) return

      this.serviceType = filter
      this.search()
    },
    sortColumn(ctx) {
      if (ctx.sortBy) {
        // this.noScroll = true
        this.sortBy = ctx.sortBy
        this.sortDesc = ctx.sortDesc
        this.fetchServiceCalls()
      }
    },
    onRowClick (item) {
      if (item.id) { this.$router.push(`/app/prod/llamadas-servicio/${item.id}`) }
    },
    rowClass (item) {
      if (!item) return
      return `OS_${item.id}`
    },
    removeFilter (key) {
      this.$store.commit('serviceCalls/removeFilter', key)
      this.search()
    },
    updateFilter (key, value) {
      this.$store.commit('serviceCalls/setFilter', {
        key,
        value
      })
      this.search()
    }
  }
}
</script>

<style scoped>

.main-container {
  background: #F7F7F8;
  color: #1a1a1a;
}

.index-body {
  min-height: 100vh;
  padding: 0 20px;
  margin: 0 auto;
  flex: 1;
}

/* Filters */

#filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4rem;
  height: 1.4rem;
  padding: 0 0.3rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #007bff;
  background-color: #fff;
  border-radius: 1rem;
}
#filter-item {
  padding: 0.2rem 0.4rem 0.2rem 0.7rem;
  color: #004085;
  background-color: #e7f1ff;
  border: 1px solid #b8daff;
  border-radius: 1rem;
}
</style>
